import React, { useEffect } from 'react';
import {
	Route, Routes, useLocation, useParams,
} from 'react-router-dom';

import { SlLEContext } from '../../global-constants';
import { ActionType, RequestType } from '../../global-interface';
import { CourseContainer } from '../CourseContainer/CourseContainer';
import { LessonContainer } from '../LessonContainer/LessonContainer';
import { SlActions } from '../../services/ActionsService/sl-actions';
import { SlDataService } from '../../services/sl-data.service';
import { TIYMaterial } from '../../components/Materials/TIY/TIYMaterial';
import { CodeCoachMaterial } from '../../components/Materials/CodeCoach/CodeCoachMaterial';
import { CodeRepoMaterial } from '../../components/Materials/CodeRepoMaterial/CodeRepoMaterial';
import { Onboarding } from '../../components/Onboarding/Onboarding';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { LocationType, MaterialType } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { Container, AuthService } from '../../../../symphony';
import { RouteGuard } from '../../../../../client/Guard/RouteGuard';
import { authGuard } from '../../../../../client/Guard/AuthGuard';

export function RouteWithCourseContainer(): JSX.Element {
	const { courseAlias } = useParams();

	const {
		onboarding$: onboarding,
		course$: course
	} = useContainerData(
		SlLEContext,
		SlDataService,
		['onboarding$', 'course$'],
	);

	const authService = Container.take('global', AuthService);

	useEffect(() => {
		const { actions$ } = Container.take(SlLEContext, SlActions);

		const searchParams = new URLSearchParams(location.search);

		const locationUrl = +searchParams.get('location') as LocationType;

		actions$.next({
			type: ActionType.OnRouteWithCourseContainerMount,
			requestType: RequestType.Parallel,
			data: {
				alias: courseAlias,
				locationUrl,
			},
		});
	}, [courseAlias, authService.isLoggedIn()]);

	const { pathname } = useLocation();

	const routes = <Routes>
		<Route
			path="/"
			element={
				<CourseContainer key={pathname?.split('/').at(-1)} />
			}
		/>
		<Route
			path="lesson/:materialId/*"
			element={<LessonContainer />}
		/>
		<Route
			path="skip-ahead/:materialId/*"
			element={<LessonContainer isSkipAhead />}
		/>
		<Route
			path="code-repo/:materialId/*"
			element={(
				<RouteGuard
					component={<CodeRepoMaterial />}
					guards={[
						authGuard({
							guardType: 'onlyAuthorized',
							navigationType: 'internal',
							redirectUrl: '/users/login',
						}),
					]}
				/>
			)}
		/>
		<Route
			path="code-coach/:materialId/*"
			element={(
				<RouteGuard
					component={<CodeCoachMaterial />}
					guards={[
						authGuard({
							guardType: 'onlyAuthorized',
							navigationType: 'internal',
							redirectUrl: '/users/login',
						}),
					]}
				/>
			)}
		/>
		<Route
			path="code-project/:materialId/*"
			element={(
				<RouteGuard
					component={<CodeCoachMaterial type={MaterialType.CodeProject} />}
					guards={[
						authGuard({
							guardType: 'onlyAuthorized',
							navigationType: 'internal',
							redirectUrl: '/users/login',
						}),
					]}
				/>
			)}
		/>
		<Route
			path="tiy/:materialId/*"
			element={(
				<RouteGuard
					component={<TIYMaterial />}
					guards={[
						authGuard({
							guardType: 'onlyAuthorized',
							navigationType: 'internal',
							redirectUrl: '/users/login',
						}),
					]}
				/>
			)}
		/>
	</Routes>;

	return onboarding ? <Onboarding /> :
		(!authService.isLoggedIn() ? routes : (course ? routes : null));
}
